import React, { useEffect, useState } from 'react';
import {
  PaymentResponseCard,
  PaymentResponseCheck,
  PaymentResponseWallet,
} from '../services/types/paymenttypes';
import { platformAndroid, platformIos } from '../utils/platform';
import PaymentAddProcess from "../usecases/shared/PaymentAddProcess";
import { AdditionalPaymentMethod } from '../usecases/payments/PaymentsList';
import {isPlatformButtonEnabled} from "../utils/config";

export const EMPTY_FIELD_REASON = 'Field is empty';

// Height of our pay buttons in iOS mode (Ionic's 3.1em at the 1rem font size of
// .btn-default). Collect.js would otherwise draw the Apple Pay button 30px tall.
const APPLE_PAY_BUTTON_HEIGHT = '3.1rem';

export const defaultConfigure: Partial<ConfigureParams> = {
  paymentSelector: '#paymentButton',
  variant: 'inline',
  styleSniffer: true,
  fields: {
    ccnumber: {
      placeholder: 'Card Number',
      selector: '#ccnumber',
    },
    ccexp: {
      placeholder: 'MM/YY',
      selector: '#ccexp',
    },
    cvv: {
      placeholder: 'CVV',
      selector: '#cvv',
    },
  },
};

type ValidationCallback = (
  changedField: string,
  isValid: boolean,
  reason: string,
) => void;

type FieldsAvailableCallback = (e: any) => void;

export type CallbackFnProps = {
  card: PaymentResponseCard;
  check: PaymentResponseCheck;
  wallet: PaymentResponseWallet;
  token: string;
  tokenType: string;
};

type CallbackFn = (e: CallbackFnProps) => void;

type ConfigureParams = {
  price?: string;
  currency?: string;
  country?: string;
  paymentSelector?: string;
  variant?: string;
  styleSniffer?: boolean;
  fields?: Record<string, any>;
  validationCallback?: ValidationCallback;
  callback: CallbackFn;
  fieldsAvailableCallback?: FieldsAvailableCallback;
};

export const getAdditionalPaymentMethods: () => AdditionalPaymentMethod[] = () => {

  if (!isPlatformButtonEnabled()) {
    return [];
  }

  const additionalPaymentMethods: AdditionalPaymentMethod[] = [];
  const fields = getFieldsForPlatform();
  if ('applePay' in fields) {additionalPaymentMethods.push("APPLE")}
  if ('googlePay' in fields) {additionalPaymentMethods.push("GOOGLE")}

  return additionalPaymentMethods as AdditionalPaymentMethod[];
}
// `selector` MUST be unique per page. Ionic keeps left-behind pages mounted in the
// DOM, so if two pages share a selector (e.g. `.platform-button`) Collect.js resolves
// the wallet field to multiple elements and throws
// "You may only mount 1 Google Pay button. '<selector>' matches 2 elements."
// Each page passes its own id (e.g. '#value-platform-button', '#passes-platform-button').
export const getFieldsForPlatform = (
  selector = '.platform-button',
): Record<string, any> => {
  if (platformIos()) {
    return {
      applePay: {
        selector,
        style: {
          'button-style': 'black',
          height: APPLE_PAY_BUTTON_HEIGHT,
          'border-radius': '0.4rem',
        },
      },
    };
  } else if (platformAndroid()) {
    return {
      googlePay: {
        selector,
      },
    };
  }
  return {
    googlePay: {
      selector,
    },
  };
};

// IMPORTANT: `configure` must be memoized with useMemo to avoid infinite loops in useCollectJS!
export function useCollectJS(configure: ConfigureParams) {
  // Use state, not a ref: assigning to a ref does not trigger a re-render, so
  // consumers (and their layout effects that call configure() to inject the
  // platform button) would keep seeing `null` until some unrelated state change
  // happened to re-render them. That was the source of the flaky button loading.
  const [collectJs, setCollectJs] = useState<any>(null);

  // NOTE ON RELOADS: this effect depends on [configure], NOT [collectJs], so the
  // setCollectJs() calls below cannot re-trigger it — there is no state -> effect
  // -> state loop. When `configure` does change identity, we take the `if` branch
  // and only *reconfigure* the already-loaded library; the Collect.js <script> is
  // appended solely in the `else` branch, i.e. only while window.CollectJS is still
  // undefined. So the library is loaded at most once under normal use.
  // (Caveat: if `configure`'s identity changes during the async gap between
  // appending the script and its onload firing, window.CollectJS is still
  // undefined and a second <script> could be appended. Keep `configure` memoized
  // to minimise that window — see the IMPORTANT note above.)
  useEffect(() => {
    if ((window as any).CollectJS) {
      // Library already present: just (re)configure. setCollectJs is called with
      // the same window.CollectJS singleton, so React bails out of the render
      // (Object.is-equal) after the first null -> object transition — no extra
      // renders, no reload.
      (window as any).CollectJS.configure(configure);
      setCollectJs((window as any).CollectJS);
    } else {
      const script = document.createElement('script');
      script.src = 'https://secure.networkmerchants.com/token/Collect.js';
      script.setAttribute(
        'data-tokenization-key',
        process.env.REACT_APP_NMI_KEY || '',
      );
      script.onload = () => {
        // setCollectJs here triggers the re-render that lets consumers observe a
        // non-null value and run their configure()/button-injection layout effect.
        (window as any).CollectJS.configure(configure);
        setCollectJs((window as any).CollectJS);
      };
      document.head.appendChild(script);
    }
  }, [configure]);

  return collectJs;
}
